// --- START OF FILE FrameworkComponents.js ---

import React, { useState, useEffect, useMemo, useRef } from 'react'; // Added useEffect for modal sync and useMemo for grouping
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BrainCircuit } from 'lucide-react';
import Badge from './Badge';
import { Card, Input, Select, TextArea } from './UIComponents';
import api from '../../../api/apiClient';
import { handleAIError } from '../../../utils/aiErrorHandler';
import { normalizeRuleStatus, getRuleStatusConfig, getRuleStatusOptions } from '../../../utils/ruleStatusNormalizer';
import { SECURITY_PILLARS } from '../../../constants';

// --- MOCK DATA (For Modal Example - Normally comes via Props) ---
const MOCK_ALL_DREADED_EVENTS = [
    { id: 'de-1', name: "Ransomware Attack", shortId: "DE001", description: "System files encrypted, ransom demanded." },
    { id: 'de-2', name: "Major Data Breach", shortId: "DE002", description: "Sensitive customer data stolen." },
    { id: 'de-3', name: "Denial of Service (DoS)", shortId: "DE003", description: "Website or critical service becomes unavailable." },
];
// --- (Keep MOCK_ALL_BUSINESS_VALUES if needed for BV modal) ---


// --- Helper ---
/* REMOVED ensureShortId
const ensureShortId = (item, prefix = '') => {
    if (item.shortId) return item.shortId;
    const idStr = String(item.id);
    const randomPart = Math.random().toString(36).substring(2, 5);
    return `${prefix}${idStr.substring(idStr.length - 4, idStr.length)}-${randomPart}`.toUpperCase();
};
*/

// --- FrameworkListItem ---
export const FrameworkListItem = ({ framework, isSelected, onSelect, selectedRuleCount, onDelete, onRename }) => {
    const { t } = useTranslation();
    const ruleCount = selectedRuleCount || 0;
    const totalRules = framework.rules?.length || 0;
    const hasRules = ruleCount > 0;

    // Calculate percentage
    const percentage = totalRules > 0 ? Math.round((ruleCount / totalRules) * 100) : 0;

    const isCustom = framework.is_predefined === false || framework.type === 'custom' || String(framework.id).startsWith('custom-') || framework.is_new;
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(framework.name);

    useEffect(() => {
        if (!isEditing) {
            setEditName(framework.name);
        }
    }, [framework.name, isEditing]);

    const handleConfirmRename = () => {
        const trimmed = editName.trim();
        if (trimmed && trimmed !== framework.name && onRename) {
            onRename(framework._id || framework.id, trimmed);
        }
        setIsEditing(false);
    };

    const handleCancelRename = () => {
        setEditName(framework.name);
        setIsEditing(false);
    };

    return (
        <Card
            isSelected={isSelected}
            onClick={onSelect}
            className="p-4 cursor-pointer hover:shadow-md"
        >
            <div className="flex justify-between items-start gap-2">
                <div className="flex-1 pr-2 min-w-0">
                    {isEditing ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleConfirmRename();
                                    if (e.key === 'Escape') handleCancelRename();
                                }}
                                onBlur={handleConfirmRename}
                                autoFocus
                                placeholder={framework.name}
                                className="flex-1 !space-y-0"
                            />
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.stopPropagation(); handleConfirmRename(); }}
                                className="p-1.5 rounded text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors flex-shrink-0"
                                title={t('common.confirm', 'Confirm')}
                                aria-label="confirm rename"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.stopPropagation(); handleCancelRename(); }}
                                className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
                                title={t('common.cancel', 'Cancel')}
                                aria-label="cancel rename"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        <h4 className="font-medium text-gray-900 truncate text-sm leading-tight" title={framework.name}>{framework.name}</h4>
                    )}
                </div>
                {isCustom && !isEditing && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditName(framework.name); setIsEditing(true); }}
                            className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title={t('common.edit', 'Edit')}
                            aria-label="edit framework"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(framework._id || framework.id, framework.name); }}
                            className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title={t('common.delete', 'Delete')}
                            aria-label="delete framework"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
            {hasRules && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-blue-600 font-semibold">
                            {ruleCount} / {totalRules} {t('securityFoundation.rules.rule')}{totalRules > 1 ? 's' : ''}
                        </div>
                        <div className={`text-xs font-bold ${percentage >= 75 ? 'text-green-600' : percentage >= 50 ? 'text-yellow-600' : percentage >= 25 ? 'text-orange-600' : 'text-red-600'}`}>
                            {percentage}%
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${percentage >= 75 ? 'bg-green-600' : percentage >= 50 ? 'bg-yellow-600' : percentage >= 25 ? 'bg-orange-600' : 'bg-red-600'}`}
                            style={{ width: `${percentage}%` }}
                        ></div>
                    </div>
                </div>
            )}
            {!hasRules && totalRules > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                        {totalRules} {t('securityFoundation.rules.rule')}{totalRules > 1 ? 's' : ''} {t('securityFoundation.status.available')}
                    </div>
                </div>
            )}
        </Card>
    );
};

// --- Available Rule Item ---
export const RuleItem = ({ rule, isSelected, onToggle, disabled }) => {
    return (
        <div
            className={`relative flex items-center justify-between px-4 py-3 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} ${isSelected ? '' : 'cursor-pointer'}`}
            onClick={!disabled ? onToggle : undefined}
        >
            <div className="flex items-center">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onToggle}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3 disabled:cursor-not-allowed"
                />
                <label className={`text-sm ${isSelected ? 'font-semibold text-blue-800' : 'text-gray-700'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    {rule.name}
                </label>
            </div>

            {isSelected && <span className="text-blue-500 font-bold">✓</span>}
        </div>
    );
};


// --- Selected Rule Card (ENHANCED UI - Full Width) ---
export const SelectedRuleCard = ({
    rule,
    onRemove,
    onStatusChange,
    onJustificationChange,
    allBusinessValues = [],
    onUpdateRuleBusinessValues,
}) => {
    const { t } = useTranslation();
    const controlsDisabled = false; // Simplified

    // 5-level status options with colors
    const statusOptions = getRuleStatusOptions(t).map(option => ({
        ...option,
        ...getRuleStatusConfig(option.value, t)
    }));

    return (
        <>
            {/* Enhanced Full-Width Card with Better Margins */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                {/* Enhanced Header */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                        <div className="flex-1 pr-4">
                            <h5 className="font-semibold text-gray-900 text-lg leading-tight" title={rule.name}>
                                {rule.name}
                            </h5>
                            <div className="flex items-center mt-1 space-x-2">
                                <p className="text-sm text-gray-600">{t('securityFoundation.sections.securityRule')}</p>
                                {rule.frameworkName && (
                                    <>
                                        <span className="text-gray-400">•</span>
                                        <div className="flex items-center space-x-1">
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: rule.frameworkColor || '#6B7280' }}
                                                title={`Framework: ${rule.frameworkName}`}
                                            ></div>
                                            <span className="text-sm font-medium" style={{ color: rule.frameworkColor || '#6B7280' }}>
                                                {rule.frameworkName}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Return to Table Button */}
                            <button
                                type="button"
                                className="text-gray-500 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                                onClick={() => {
                                    const rowEl = document.getElementById(`summary-row-${rule.id}`);
                                    if (rowEl) {
                                        rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        // Temporary highlight
                                        rowEl.classList.add('!bg-yellow-100', 'transition-colors', 'duration-1000');
                                        setTimeout(() => rowEl.classList.remove('!bg-yellow-100', 'transition-colors', 'duration-1000'), 1500);
                                    } else {
                                        document.getElementById('summary-table-section')?.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }}
                                title={t('securityFoundation.buttons.backToSummary', 'Retour au tableau')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                            </button>
                            {/* Remove Button */}
                            {!controlsDisabled && (
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
                                    onClick={onRemove}
                                    title="Supprimer"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Enhanced Card Body */}
                <div className="px-6 py-5 space-y-4">
                    {/* Status Radio Group */}
                    <div >
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('securityFoundation.sections.status')}</label>
                        <div className="flex flex-wrap items-center gap-4 pt-1">
                            {statusOptions.map(option => (
                                <div key={option.value} className="flex items-center">
                                    <input
                                        id={`status-${rule.id}-${option.value}`}
                                        name={`status-${rule.id}`}
                                        type="radio"
                                        value={option.value}
                                        checked={normalizeRuleStatus(rule.status) === option.value}
                                        onChange={onStatusChange}
                                        disabled={controlsDisabled}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                    />
                                    <label htmlFor={`status-${rule.id}-${option.value}`} className="ml-2 flex items-center text-sm text-gray-700 cursor-pointer">
                                        <span className={`w-3 h-3 rounded-full mr-1.5 ${option.dotColor.replace('text-', 'bg-')}`}></span>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Justification Input (Now below Status) */}
                    <div >
                        <label htmlFor={`justification-${rule.id}`} className="block text-xs font-medium text-gray-600 mb-1">{t('securityFoundation.sections.justification')}</label>
                        <Input id={`justification-${rule.id}`} type="text" value={rule.justification || ''} onChange={onJustificationChange} placeholder={t('securityFoundation.placeholders.optional')} disabled={controlsDisabled}
                            className={`block w-full text-sm ${controlsDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                    </div>

                    </div>
                </div>
        </>
    );
};

// --- ACG Assistant Button Component ---
const ACGAssistantButton = ({
    rule,
    allDreadedEvents,
    onAutoMatch,
    disabled,
    variant = 'small',
    onAIResultsChange, // Callback to pass suggestions to parent
    analysisId // Added prop for permission check
}) => {
    const { t } = useTranslation();
    const [isMatching, setIsMatching] = useState(false);

    const handleManualAIMatch = async () => {
        if (disabled || isMatching) return;

        setIsMatching(true);

        try {
            // Prepare the context for AI matching
            const context = {
                ruleName: rule.name,
                ruleDescription: rule.description || '',
                dreadedEvents: allDreadedEvents.map(event => ({
                    id: event.id,
                    name: event.name,
                    description: event.description || '',
                    severity: event.severity || '',
                    securityPillar: event.securityPillar || ''
                })),
                analysisId: analysisId
            };

            // Call the AI matching API
            const result = await api.post('/ai/match-rule-to-dreaded-events', context);

            if (result.success && result.data && Array.isArray(result.data.matchedEventIds)) {
                // Convert both to strings for comparison
                const matchedEventIds = result.data.matchedEventIds.map(id => String(id));
                const matchedEvents = allDreadedEvents.filter(event =>
                    matchedEventIds.includes(String(event.id))
                );

                // Prepare AI suggestions with reasoning
                const suggestions = matchedEvents.map(event => ({
                    event: event,
                    reasoning: result.data.reasoning[String(event.id)] || 'Aucune justification fournie',
                    selected: false
                }));

                // Pass suggestions to parent
                if (suggestions.length > 0) {
                    if (onAIResultsChange) {
                        onAIResultsChange(suggestions);
                    }
                } else {
                    window.alert('Aucun événement pertinent trouvé pour cette règle.');
                }
            } else {
                throw new Error(result.message || 'Invalid response format');
            }
        } catch (error) {
            console.error('Error during manual AI matching:', error);
            // Use centralized AI error handler for user-friendly messages
            handleAIError(error, { language: 'fr' });
            let errorMessage = 'Erreur lors de l\'analyse';
            if (error.status === 401) {
                errorMessage = 'Erreur d\'authentification';
            } else if (error.message) {
                errorMessage = `Erreur: ${error.message}`;
            }
            window.alert(errorMessage);
        } finally {
            setIsMatching(false);
        }
    };

    return (
        <button
            onClick={handleManualAIMatch}
            disabled={disabled || isMatching}
            className={`inline-flex items-center ${variant === 'large' ? 'px-6 py-3 text-sm' : 'px-3 py-2 text-xs'} font-medium rounded-lg transition-all duration-200 transform hover:scale-105 ${disabled || isMatching
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : variant === 'large'
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl'
                    : 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 hover:from-purple-200 hover:to-blue-200 border border-purple-200'
                }`}
            title="Assistant ACG - Suggestions intelligentes"
        >
            {isMatching ? (
                <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </>
            ) : (
                <BrainCircuit className={variant === 'large' ? 'w-5 h-5' : 'w-4 h-4'} />
            )}
        </button>
    );
};


// --- DreadedEventSelectorModal (NEW COMPONENT) ---
const DreadedEventSelectorModal = ({
    isOpen,
    onClose,
    onSave,
    allDreadedEvents = [],
    initialSelectedValueIds = [],
    currentRuleName
}) => {
    const { t } = useTranslation();
    const [tempSelectedEvents, setTempSelectedEvents] = useState(initialSelectedValueIds);

    // Re-sync state if modal reopens with different initial values
    useEffect(() => {
        if (isOpen) {
            setTempSelectedEvents(initialSelectedValueIds);
        }
    }, [initialSelectedValueIds, isOpen]);

    const toggleEvent = (eventId) => {
        setTempSelectedEvents(prev =>
            prev.includes(eventId)
                ? prev.filter(id => id !== eventId)
                : [...prev, eventId]
        );
    };

    const handleSaveClick = () => {
        onSave(tempSelectedEvents);
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                {/* Modal Header */}
                <div className="p-5 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Associer des événements redoutés</h3>
                    <p className="text-sm text-gray-500 mt-1">Règle: <span className="font-semibold">{currentRuleName}</span></p>
                </div>
                {/* Modal Body */}
                <div className="p-6 max-h-96 overflow-y-auto">
                    {allDreadedEvents.length > 0 ? (
                        <div className="space-y-3">
                            {allDreadedEvents.map(value => (
                                <div key={value.id} className="flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            id={`modal-de-${value.id}`}
                                            type="checkbox"
                                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                            checked={tempSelectedEvents.includes(value.id)}
                                            onChange={() => toggleEvent(value.id)}
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor={`modal-de-${value.id}`} className="font-medium text-gray-700 cursor-pointer">
                                            {value.name}
                                        </label>
                                        {value.description && (<p className="text-xs text-gray-500">{value.description}</p>)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">Aucun événement redouté disponible</p>
                    )}
                </div>
                {/* Modal Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                    <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Annuler</button>
                    <button
                        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                        onClick={handleSaveClick}
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// --- BusinessValueSelectorModal (No changes needed) ---
const BusinessValueSelectorModal = ({ isOpen, onClose, onSave, allBusinessValues = [], initialSelectedValueIds = [], currentRuleName }) => {
    // ... (same as before, uses state internally) ...
    const [tempSelectedValues, setTempSelectedValues] = useState(initialSelectedValueIds);

    useEffect(() => { if (isOpen) { setTempSelectedValues(initialSelectedValueIds); } }, [initialSelectedValueIds, isOpen]);

    const toggleValue = (valueId) => { setTempSelectedValues(prev => prev.includes(valueId) ? prev.filter(id => id !== valueId) : [...prev, valueId]); };
    const handleSaveClick = () => { onSave(tempSelectedValues); };

    if (!isOpen) return null;
    return ReactDOM.createPortal(<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"> <div className="bg-white rounded-lg shadow-xl max-w-md w-full"> {/* Header */} <div className="p-5 border-b border-gray-200"><h3 className="text-lg font-medium text-gray-900">Lier Valeurs Métier</h3><p className="text-sm text-gray-500 mt-1">Règle: <span className="font-semibold">{currentRuleName}</span></p></div> {/* Body */} <div className="p-6 max-h-96 overflow-y-auto">{allBusinessValues.length > 0 ? (<div className="space-y-3">{allBusinessValues.map(value => (<div key={value.id} className="flex items-start"><div className="flex items-center h-5"><input id={`modal-bv-${value.id}`} type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={tempSelectedValues.includes(value.id)} onChange={() => toggleValue(value.id)} /></div><div className="ml-3 text-sm"><label htmlFor={`modal-bv-${value.id}`} className="font-medium text-gray-700">{/* <span className="text-blue-600 mr-1">{ensureShortId(value, 'BV')}</span> */} {value.name}</label>{(value.supportAssets || []).length > 0 && (<div className="mt-1 text-xs text-gray-500">Biens: {(value.supportAssets).map(a => a.name || `Asset ${a.id}`).join(', ')}</div>)}</div></div>))}</div>) : (<p className="text-gray-500 text-center py-4">Aucune valeur métier.</p>)}</div> {/* Footer */} <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3"><button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Annuler</button><button className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSaveClick}>Confirmer</button></div> </div> </div>, document.body);
};

// --- NEW: Rule Description Modal Component ---
export const RuleDescriptionModal = ({ isOpen, onClose, ruleName, ruleDescription }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"> {/* Higher z-index than other modals */}
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full relative animate-fade-in-scale">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors"
                    title="Fermer"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Header */}
                <div className="p-5 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Description de la règle</h3>
                    <p className="text-sm text-gray-600 mt-1 font-semibold truncate" title={ruleName}>{ruleName || 'Règle'}</p>
                </div>

                {/* Body */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{ruleDescription || 'Aucune description disponible'}</p>
                </div>

                {/* Optional Footer (if needed for actions) */}
                {/* <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end"> */}
                {/*     <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Fermer</button> */}
                {/* </div> */}
            </div>
        </div>
    );
};


// --- Helper Functions ---

// Map severity strings to G-levels and Tailwind classes based on the image
const getSeverityLevelInfo = (severity, t) => {
    const lowerSeverity = severity?.toLowerCase() || '';
    // UPDATED: Check against actual data values first, then potentially French fallbacks if needed
    switch (lowerSeverity) {
        case 'catastrophic': // From your data
            return { level: 'G5', label: t('severity.catastrophic'), textClass: 'text-white', bgClass: 'bg-black' };
        case 'critical':     // From your data
            return { level: 'G4', label: t('severity.critical'), textClass: 'text-white', bgClass: 'bg-red-600' };
        // Assuming 'high' severity might map to G3 - Grave
        case 'major':
        case 'majeure':        // Keep French as fallback/alternative
            return { level: 'G3', label: t('severity.major'), textClass: 'text-black', bgClass: 'bg-yellow-500' };
        case 'moderate':     // From your data
        case 'significative': // Keep French as fallback/alternative
            return { level: 'G2', label: t('severity.moderate'), textClass: 'text-black', bgClass: 'bg-yellow-200' };
        // Assuming 'low' severity might map to G1 - Mineure
        case 'minor':
        case 'mineure':      // Keep French as fallback/alternative
            return { level: 'G1', label: t('severity.minor'), textClass: 'text-black', bgClass: 'bg-cyan-300' };
        default:
            console.warn(`Unknown severity value encountered: '${severity}'`); // Added warning
            return { level: 'N/A', label: 'Non défini', textClass: 'text-gray-800', bgClass: 'bg-gray-200' };
    }
};

// Helper to get status label using 5-level system
const getStatusLabel = (status, t) => {
    const level = normalizeRuleStatus(status);
    return getRuleStatusConfig(level, t).label;
};

// --- Summary Table Component ---
export const SecurityFrameworkSummaryTable = ({
    rulesToDisplay = [],
    onModifyRule,
    onDeleteRule,
    ruleGapComments = {},
    onUpdateRuleGapComment,
    allDreadedEvents = [],
    ruleDreadedEvents = {},
    onUpdateRuleDreadedEvents,
    onCreateDreadedEvent,
    allBusinessValues = [],
}) => {
    const { t } = useTranslation();

    const [isDeModalOpen, setIsDeModalOpen] = useState(false);
    const [deModalRuleId, setDeModalRuleId] = useState(null);

    const [isCreateDeModalOpen, setIsCreateDeModalOpen] = useState(false);
    const [createDeModalRuleId, setCreateDeModalRuleId] = useState(null);

    const openDeModal = (ruleId) => {
        setDeModalRuleId(ruleId);
        setIsDeModalOpen(true);
    };
    const closeDeModal = () => {
        setIsDeModalOpen(false);
        setDeModalRuleId(null);
    };
    const handleDeModalSave = (selectedIds) => {
        if (deModalRuleId) onUpdateRuleDreadedEvents(deModalRuleId, selectedIds);
        closeDeModal();
    };

    const openCreateDeModal = (ruleId) => {
        setCreateDeModalRuleId(ruleId);
        setIsCreateDeModalOpen(true);
    };
    const closeCreateDeModal = () => {
        setIsCreateDeModalOpen(false);
        setCreateDeModalRuleId(null);
    };
    const handleCreateDeModalSave = async (name, description, businessValueId, securityPillar, severity, severityJustification, impacts) => {
        try {
            const newDE = await onCreateDreadedEvent(name, description, businessValueId, securityPillar, severity, severityJustification, impacts);
            if (newDE && createDeModalRuleId) {
                const current = ruleDreadedEvents[createDeModalRuleId] || [];
                onUpdateRuleDreadedEvents(createDeModalRuleId, [...current, newDE.id]);
            }
            closeCreateDeModal();
        } catch (e) {
            console.error('Failed to create DE:', e);
        }
    };

    const exportToJson = () => {
        const jsonData = rulesToDisplay.map(rule => ({
            frameworkId: rule.frameworkId, frameworkName: rule.frameworkName, ruleId: rule.id, ruleName: rule.name, status: rule.status, statusLabel: getStatusLabel(rule.status, t), justification: rule.justification || "N/A",
        }));
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'summary.json'; a.click(); URL.revokeObjectURL(url);
    };

    const groupedRules = useMemo(() => {
        const groups = new Map();
        rulesToDisplay.forEach(rule => {
            if (!groups.has(rule.frameworkName)) {
                groups.set(rule.frameworkName, []);
            }
            groups.get(rule.frameworkName).push(rule);
        });
        return Array.from(groups.entries()).map(([frameworkName, rules]) => ({ frameworkName, rules }));
    }, [rulesToDisplay]);

    const regularBusinessValues = allBusinessValues.filter(v => !v.isSystem);

    return (
        <>
            <div className="mt-8 bg-white rounded-lg border border-gray-200 shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Résumé</h3>
                </div>

                <div className="overflow-x-auto summary-table-print-area">
                    <table className="min-w-full divide-y divide-gray-200 border-collapse">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 border-gray-200">{t('securityFoundation.summary.headers.framework')}</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 border-gray-200">{t('securityFoundation.summary.headers.rule')}</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 border-gray-200">{t('securityFoundation.summary.headers.status')}</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 border-gray-200">{t('securityFoundation.summary.headers.gapComment')}</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b-2 border-gray-200">{t('securityFoundation.summary.headers.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {groupedRules.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500">
                                        {t('securityFoundation.summary.noRulesSelected')}
                                    </td>
                                </tr>
                            ) : (
                                groupedRules.map(({ frameworkName, rules }) => (
                                    rules.map((item, ruleIndexInGroup) => (
                                        <tr id={`summary-row-${item.id}`} key={`${item.frameworkId}-${item.id}`} className="hover:bg-gray-50">
                                            {/* 1. Framework Name */}
                                            {ruleIndexInGroup === 0 && (
                                                <td
                                                    rowSpan={rules.length}
                                                    className="px-4 py-4 align-top text-sm font-medium text-gray-700 whitespace-nowrap border-r border-l border-b border-gray-200 w-48 bg-gray-50"
                                                >
                                                    {frameworkName}
                                                </td>
                                            )}

                                            {/* 2. Rule Name */}
                                            <td className="px-4 py-4 align-top text-sm font-medium text-gray-800 border-r border-b border-gray-200 max-w-xs break-words">
                                                {item.name}
                                            </td>

                                            {/* 3. Status + Justification */}
                                            <td className="px-4 py-4 align-top text-sm border-r border-b border-gray-200 min-w-[160px]">
                                                {(() => {
                                                    const config = getRuleStatusConfig(normalizeRuleStatus(item.status), t);
                                                    return (
                                                        <>
                                                            <span className={`inline-flex items-center text-xs leading-5 font-semibold rounded-full px-2 py-0.5 ${config.bgClass} ${config.textClass}`}>
                                                                {config.label}
                                                            </span>
                                                            <div className="mt-1.5 text-xs text-gray-400">
                                                                {item.justification || <span className="italic">—</span>}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </td>

                                            {/* 4. Commentaire sur l'écart */}
                                            <td className="px-4 py-4 align-top border-r border-b border-gray-200 min-w-[220px] max-w-[300px]">
                                                <textarea
                                                    defaultValue={ruleGapComments[item.id] || ''}
                                                    onBlur={(e) => onUpdateRuleGapComment(item.id, e.target.value)}
                                                    className="w-full border border-gray-200 rounded p-1.5 text-xs resize-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
                                                    rows={2}
                                                    placeholder={t('securityFoundation.summary.gapCommentPlaceholder')}
                                                />
                                                <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                                                    {(ruleDreadedEvents[item.id] || [])
                                                        .filter(deId => deId !== '__v')
                                                        .map(deId => {
                                                            const de = allDreadedEvents.find(d => d.id === deId);
                                                            if (!de) return null;
                                                            const isNonIntentional = de.businessValue === '__global__';
                                                            return (
                                                                <span key={de.id} className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${isNonIntentional ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                                                                    {de.name}
                                                                </span>
                                                            );
                                                        })}
                                                    <button
                                                        onClick={() => openDeModal(item.id)}
                                                        className="text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-1.5 py-0.5 rounded transition-colors"
                                                        title={t('securityFoundation.summary.linkDe')}
                                                    >
                                                        + Link
                                                    </button>
                                                    <button
                                                        onClick={() => openCreateDeModal(item.id)}
                                                        className="text-xs text-green-600 hover:text-green-800 hover:bg-green-50 px-1.5 py-0.5 rounded transition-colors"
                                                        title={t('securityFoundation.summary.createDe')}
                                                    >
                                                        + New
                                                    </button>
                                                </div>
                                            </td>

                                            {/* 5. Actions */}
                                            <td className="px-4 py-4 align-top text-sm whitespace-nowrap space-x-3 border-b border-r border-gray-200">
                                                <button
                                                    onClick={() => onModifyRule(item.id)}
                                                    className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-100 transition duration-150 ease-in-out"
                                                    title={t('securityFoundation.buttons.modifyRule')}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => onDeleteRule(item.id)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-100 transition duration-150 ease-in-out"
                                                    title={t('securityFoundation.buttons.deleteRule')}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* DE Selector Modal */}
            {isDeModalOpen && (
                <DreadedEventSelectorModal
                    isOpen={isDeModalOpen}
                    onClose={closeDeModal}
                    onSave={handleDeModalSave}
                    allDreadedEvents={allDreadedEvents}
                    initialSelectedValueIds={deModalRuleId ? (ruleDreadedEvents[deModalRuleId] || []).filter(id => id !== '__v') : []}
                    currentRuleName={deModalRuleId ? (rulesToDisplay.find(r => r.id === deModalRuleId)?.name || '') : ''}
                />
            )}

            {/* Create DE Modal */}
            {isCreateDeModalOpen && (
                <CreateDreadedEventModal
                    isOpen={isCreateDeModalOpen}
                    onClose={closeCreateDeModal}
                    onSave={handleCreateDeModalSave}
                    businessValues={regularBusinessValues}
                />
            )}
        </>
    );
};

// --- CreateDreadedEventModal ---
const CreateDreadedEventModal = ({ isOpen, onClose, onSave, businessValues = [] }) => {
    const { t } = useTranslation();
    const [eventType, setEventType] = useState('er');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedBvId, setSelectedBvId] = useState(businessValues[0]?.id || '');
    const [securityPillar, setSecurityPillar] = useState('');
    const [severity, setSeverity] = useState('moderate');
    const [severityJustification, setSeverityJustification] = useState('');
    const [impacts, setImpacts] = useState([]);

    const pillarColors = {
        confidentialite: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
        integrite: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
        disponibilite: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-300' },
        tracabilite: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300' },
        preuve: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300' },
        Auditabilite: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
    };

    const severityColors = {
        minor: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300' },
        moderate: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
        major: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300' },
        critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
        catastrophic: { bg: 'bg-slate-900', text: 'text-slate-50', border: 'border-slate-700' },
    };

    const severityOptions = [
        { value: 'minor', label: t('severity.minor') },
        { value: 'moderate', label: t('severity.moderate') },
        { value: 'major', label: t('severity.major') },
        { value: 'critical', label: t('severity.critical') },
        { value: 'catastrophic', label: t('severity.catastrophic') },
    ];

    const impactOptions = [
        { value: 'missions_services', label: t('impacts.missions_services') },
        { value: 'humain_materiel_environnemental', label: t('impacts.humain_materiel_environnemental') },
        { value: 'gouvernance', label: t('impacts.gouvernance') },
        { value: 'financier', label: t('impacts.financier') },
        { value: 'juridique', label: t('impacts.juridique') },
        { value: 'image_confiance', label: t('impacts.image_confiance') },
    ];

    useEffect(() => {
        if (isOpen) {
            setName('');
            setDescription('');
            setEventType('er');
            setSelectedBvId(businessValues[0]?.id || '');
            setSecurityPillar('');
            setSeverity('moderate');
            setSeverityJustification('');
            setImpacts([]);
        }
    }, [isOpen, businessValues]);

    const toggleImpact = (value) => {
        setImpacts(prev => {
            const exists = prev.find(i => i.impact === value);
            if (exists) return prev.filter(i => i.impact !== value);
            return [...prev, { impact: value, justification: '' }];
        });
    };

    const updateImpactJustification = (value, justification) => {
        setImpacts(prev => prev.map(i => i.impact === value ? { ...i, justification } : i));
    };

    const handleSave = () => {
        if (!name.trim()) return;
        const bvId = eventType === 'non-intentional' ? '__global__' : selectedBvId;
        onSave(name.trim(), description.trim(), bvId, securityPillar, severity, severityJustification, impacts);
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
                <div className="p-5 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">{t('securityFoundation.createDe.title')}</h3>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {/* Type toggle */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityFoundation.createDe.type')}</label>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input type="radio" name="de-type" value="er" checked={eventType === 'er'} onChange={() => setEventType('er')} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700">{t('securityFoundation.createDe.typeER')}</span>
                            </label>
                            <label className="flex items-center">
                                <input type="radio" name="de-type" value="non-intentional" checked={eventType === 'non-intentional'} onChange={() => setEventType('non-intentional')} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700">{t('securityFoundation.createDe.typeNonIntentional')}</span>
                            </label>
                        </div>
                    </div>

                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityFoundation.createDe.name')} *</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none" placeholder={t('securityFoundation.createDe.namePlaceholder')} />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityFoundation.createDe.description')}</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none" rows={2} placeholder={t('securityFoundation.createDe.descriptionPlaceholder')} />
                    </div>

                    {/* BV dropdown (ER only) */}
                    {eventType === 'er' && businessValues.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityFoundation.createDe.businessValue')}</label>
                            <select value={selectedBvId} onChange={(e) => { setSelectedBvId(e.target.value); setSecurityPillar(''); }} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none">
                                {businessValues.map(bv => (
                                    <option key={bv.id} value={bv.id}>{bv.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Security Pillar */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('dreadedEvents.fields.securityPillar')}</label>
                        <div className="grid grid-cols-3 gap-2">
                            {SECURITY_PILLARS.map(pillar => {
                                const colors = pillarColors[pillar.id] || { bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-300' };
                                const isSelected = securityPillar === pillar.id;
                                return (
                                    <button
                                        key={pillar.id}
                                        type="button"
                                        onClick={() => setSecurityPillar(pillar.id)}
                                        className={`p-2 rounded-md border text-center transition-all text-xs
                                            ${isSelected ? 'ring-2 ring-offset-1 ring-purple-500 ' + colors.bg + ' ' + colors.border : colors.bg + ' ' + colors.text + ' ' + colors.border}
                                            hover:opacity-90`}
                                    >
                                        <div className="font-semibold">{pillar.name}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Severity */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('dreadedEvents.fields.severity')}</label>
                        <div className="grid grid-cols-5 gap-2">
                            {severityOptions.map(option => {
                                const colors = severityColors[option.value] || { bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-300' };
                                const isSelected = severity === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setSeverity(option.value)}
                                        className={`p-2 rounded-md border text-center transition-all
                                            ${colors.bg} ${colors.text} ${colors.border}
                                            ${isSelected ? 'ring-2 ring-offset-1 ring-purple-500' : ''}
                                            hover:opacity-90`}
                                    >
                                        <div className="font-medium text-xs">{option.label}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Severity Justification */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('dreadedEvents.fields.severityJustification')}</label>
                        <textarea value={severityJustification} onChange={(e) => setSeverityJustification(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none" rows={2} placeholder={t('dreadedEvents.placeholders.severityJustification')} />
                    </div>

                    {/* Impacts */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('dreadedEvents.fields.impacts')}</label>
                        <div className="grid grid-cols-1 gap-2 mt-1">
                            {impactOptions.map(impactOption => {
                                const isSelected = impacts.some(i => i.impact === impactOption.value);
                                const impactObj = impacts.find(i => i.impact === impactOption.value);
                                return (
                                    <div key={impactOption.value} className="space-y-1">
                                        <div className={`flex items-center p-2 rounded border ${isSelected ? 'bg-purple-50 border-purple-300' : 'border-gray-300'}`}>
                                            <input
                                                type="checkbox"
                                                id={`modal-impact-${impactOption.value}`}
                                                checked={isSelected}
                                                onChange={() => toggleImpact(impactOption.value)}
                                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor={`modal-impact-${impactOption.value}`} className="ml-2 block text-xs text-gray-700 cursor-pointer flex-1">
                                                {impactOption.label}
                                            </label>
                                        </div>
                                        {isSelected && (
                                            <div className="ml-6">
                                                <textarea
                                                    value={impactObj?.justification || ''}
                                                    onChange={(e) => updateImpactJustification(impactOption.value, e.target.value)}
                                                    placeholder={t('securityFoundation.createDe.impactJustificationPlaceholder')}
                                                    rows={1}
                                                    className="w-full border border-gray-200 rounded p-1.5 text-xs resize-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                    <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>{t('securityFoundation.buttons.cancel')}</button>
                    <button className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50" onClick={handleSave} disabled={!name.trim() || !securityPillar}>{t('securityFoundation.buttons.confirm')}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};
